/**
 * Remove duplicate values from an array.
 * @example unique([1, 2, 2, 3]) // [1, 2, 3]
 */
export function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

/**
 * Chunk an array into smaller arrays of a given size.
 * @example chunk([1,2,3,4,5], 2) // [[1,2],[3,4],[5]]
 */
export function chunk<T>(arr: T[], size: number): T[][] {
  if (size <= 0) throw new RangeError("Chunk size must be greater than 0");
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

/**
 * Flatten a nested array by one level (or deeply).
 * @example flatten([[1,2],[3,[4]]]) // [1,2,3,[4]]
 * @example flatten([[1,2],[3,[4]]], true) // [1,2,3,4]
 */
export function flatten<T>(arr: (T | T[])[], deep = false): T[] {
  return deep ? arr.flat(Infinity) as T[] : arr.flat() as T[];
}

/**
 * Get the intersection of two arrays.
 * @example intersection([1,2,3], [2,3,4]) // [2,3]
 */
export function intersection<T>(a: T[], b: T[]): T[] {
  const setB = new Set(b);
  return a.filter((x) => setB.has(x));
}

/**
 * Get the difference of two arrays (elements in `a` not in `b`).
 * @example difference([1,2,3], [2,3,4]) // [1]
 */
export function difference<T>(a: T[], b: T[]): T[] {
  const setB = new Set(b);
  return a.filter((x) => !setB.has(x));
}

/**
 * Shuffle an array using Fisher-Yates algorithm (returns a new array).
 */
export function shuffle<T>(arr: T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j] as T, result[i] as T];
  }
  return result;
}

/**
 * Group array elements by a key derived from a callback.
 * @example groupBy([{type:'a'},{type:'b'},{type:'a'}], x => x.type)
 * // { a: [{type:'a'},{type:'a'}], b: [{type:'b'}] }
 */
export function groupBy<T>(arr: T[], keyFn: (item: T) => string): Record<string, T[]> {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const key = keyFn(item);
    (acc[key] ??= []).push(item);
    return acc;
  }, {});
}

/**
 * Sum all numbers in an array.
 */
export function sum(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0);
}

/**
 * Get the min and max values of a numeric array.
 */
export function minMax(arr: number[]): { min: number; max: number } {
  if (!arr.length) throw new Error("Array must not be empty");
  return { min: Math.min(...arr), max: Math.max(...arr) };
}

/**
 * Pick a random element from an array.
 */
export function sample<T>(arr: T[]): T {
  if (!arr.length) throw new Error("Array must not be empty");
  return arr[Math.floor(Math.random() * arr.length)] as T;
}

/**
 * Create an array of numbers from start to end (inclusive).
 * @example range(1, 5) // [1, 2, 3, 4, 5]
 * @example range(0, 10, 2) // [0, 2, 4, 6, 8, 10]
 */
export function range(start: number, end: number, step = 1): number[] {
  const result: number[] = [];
  for (let i = start; i <= end; i += step) result.push(i);
  return result;
}
